import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";

import { Dialog, DialogHeader, Icon, SelectableCard } from "@/ds";

import { ConflitoIgnorarDialog } from "../conflito-ignorar-dialog";
import { ConflitoPassos } from "../conflito-passos";
import { TagSimulacao } from "../simulacao/tag-simulacao";
import { RevisaoStep } from "./components/revisao-step";
import { SubstitutoStep } from "./components/substituto-step";
import {
  COMO_RESOLVER_LABEL,
  CONFLITO_LOAD_ERROR_MESSAGE,
  OPCAO_IGNORAR_DESCRIPTION,
  OPCAO_IGNORAR_TITLE,
  OPCAO_REMANEJAR_DESCRIPTION,
  OPCAO_REMANEJAR_TITLE,
  QUAL_CONFLITO_LABEL,
  RECOMENDADO_LABEL,
  STEP_SELECIONAR_LABEL,
} from "./resolver-conflito.constants";
import styles from "../../conflito.module.scss";
import type {
  ConflitoResumoModel,
  ResolverConflitoDialogViewModel,
} from "./types-resolver-conflito";

function ConflitoResumo({ resumo }: { resumo: ConflitoResumoModel }) {
  return (
    <section className={styles.resumo} data-testid="resolver-conflito-resumo">
      <div className={styles.resumoPessoa}>
        <span className={styles.resumoNome}>{resumo.nome}</span>
        {resumo.time ? <span className={styles.resumoPapel}>{resumo.time}</span> : null}
      </div>
      <span className={styles.resumoAlerta}>
        <Icon name="exclamation-triangle" size={10.5} />
        Este profissional está alocado em 2 obras com datas sobrepostas
      </span>
      <ul className={styles.resumoObras}>
        {resumo.obras.map((obra) => (
          <li key={obra.key} className={styles.resumoObra}>
            <span className={styles.resumoObraNome}>{obra.titulo}</span>
            <span>{obra.intervalo}</span>
          </li>
        ))}
      </ul>
      <span className={styles.sobreposicao}>
        <i className="pi pi-times-circle" aria-hidden />
        Sobreposição identificada: {resumo.sobreposicao}
      </span>
    </section>
  );
}

export function ResolverConflitoDialogView({
  etapa,
  titulo,
  orientacao,
  simulando,
  isSubmitting,
  semConflito,
  serverError,
  resumo,
  escolha,
  proposta,
  substituto,
  revisao,
  corpoRef,
  erroRef,
  ignorar,
  rodape,
  fechar,
}: ResolverConflitoDialogViewModel) {
  if (ignorar) return <ConflitoIgnorarDialog {...ignorar} />;

  const { voltar, primario } = rodape;

  return (
    <Dialog
      visible
      size={etapa === "revisao" ? "xl" : "md"}
      onHide={fechar}
      locked={isSubmitting}
      header={
        <DialogHeader
          title={
            <>
              {titulo}
              {simulando ? <TagSimulacao quebraNoMobile /> : null}
            </>
          }
          subtitle={orientacao}
          data-testid="resolver-conflito-guidance"
        />
      }
      footer={
        <div className={styles.footer}>
          {voltar ? (
            <Button
              type="button"
              label="Voltar"
              severity="secondary"
              text
              onClick={voltar}
              disabled={isSubmitting}
              data-testid="resolver-conflito-voltar"
            />
          ) : (
            <Button
              type="button"
              label="Cancelar"
              severity="secondary"
              text
              onClick={fechar}
              disabled={isSubmitting}
              data-testid="resolver-conflito-cancel"
            />
          )}
          {primario ? (
            <Button
              type="button"
              label={primario.rotulo}
              onClick={primario.acionar}
              loading={primario.carregando}
              disabled={primario.desabilitado}
              data-testid={primario.testId}
            />
          ) : null}
        </div>
      }
      data-testid="resolver-conflito-dialog"
    >
      <div ref={corpoRef} className={styles.body}>
        {serverError ? (
          <div ref={erroRef}>
            <Message severity="error" text={serverError} data-testid="resolver-conflito-error" />
          </div>
        ) : null}
        {semConflito ? (
          <Message
            severity="error"
            text={CONFLITO_LOAD_ERROR_MESSAGE}
            data-testid="resolver-conflito-load-error"
          />
        ) : null}

        {resumo && etapa === "conflito" ? (
          <>
            <section className={styles.resumo}>
              <div className={styles.resumoPessoa}>
                <span className={styles.resumoNome}>{resumo.nome}</span>
                {resumo.time ? <span className={styles.resumoPapel}>{resumo.time}</span> : null}
              </div>
              <span className={styles.resumoAlerta}>
                <Icon name="exclamation-triangle" size={10.5} />
                Este profissional tem {escolha.total} conflitos no cronograma
              </span>
            </section>
            <fieldset className={styles.secao}>
              <legend className={styles.secaoTitulo}>{QUAL_CONFLITO_LABEL}</legend>
              <div className={styles.opcoes}>
                {escolha.opcoes.map((opcao, index) => (
                  <SelectableCard
                    key={opcao.key}
                    value={index}
                    selected={opcao.marcada}
                    name="resolver-conflito-escolha"
                    onSelect={escolha.escolher}
                    data-testid="resolver-conflito-escolha"
                  >
                    <span className={styles.opcaoCorpo}>
                      <span className={styles.opcaoTitulo}>{opcao.titulo}</span>
                      <span className={styles.opcaoDescricao}>{opcao.descricao}</span>
                    </span>
                  </SelectableCard>
                ))}
              </div>
            </fieldset>
          </>
        ) : null}

        {resumo && etapa === "proposta" ? (
          <>
            <ConflitoResumo resumo={resumo} />
            <fieldset className={styles.secao}>
              <legend className={styles.secaoTitulo}>{COMO_RESOLVER_LABEL}</legend>
              <div className={styles.opcoes}>
                <SelectableCard
                  value="remanejar"
                  selected={proposta.tratamento === "remanejar"}
                  name="resolver-conflito-tratamento"
                  onSelect={proposta.escolher}
                  data-testid="resolver-conflito-opcao-remanejar"
                >
                  <span className={styles.opcaoCorpo}>
                    <span className={styles.opcaoTituloLinha}>
                      <span className={styles.opcaoTitulo}>
                        <i className="pi pi-times-circle" aria-hidden />
                        {OPCAO_REMANEJAR_TITLE}
                      </span>
                      <Tag className="p-tag-xs" severity="info" value={RECOMENDADO_LABEL} />
                    </span>
                    <span className={styles.opcaoDescricao}>{OPCAO_REMANEJAR_DESCRIPTION}</span>
                  </span>
                </SelectableCard>
                <SelectableCard
                  value="ignorar"
                  selected={proposta.tratamento === "ignorar"}
                  name="resolver-conflito-tratamento"
                  onSelect={proposta.escolher}
                  data-testid="resolver-conflito-opcao-ignorar"
                >
                  <span className={styles.opcaoCorpo}>
                    <span className={styles.opcaoTitulo}>
                      <i className="pi pi-eye-slash" aria-hidden />
                      {OPCAO_IGNORAR_TITLE}
                    </span>
                    <span className={styles.opcaoDescricao}>{OPCAO_IGNORAR_DESCRIPTION}</span>
                  </span>
                </SelectableCard>
              </div>
            </fieldset>
          </>
        ) : null}

        {substituto || revisao ? (
          <ConflitoPassos
            etapa={revisao ? "revisao" : "selecionar"}
            selecionarLabel={STEP_SELECIONAR_LABEL}
          />
        ) : null}
        {substituto ? <SubstitutoStep {...substituto} /> : null}
        {revisao ? <RevisaoStep {...revisao} /> : null}
      </div>
    </Dialog>
  );
}
